import { createFileRoute, Link } from "@tanstack/react-router";
import { Heart, Users, Star, Phone } from "lucide-react";
import { canonical } from "@/lib/seo";

export const Route = createFileRoute("/about")({
  head: () => ({
    meta: [
      { title: "About Malak BnB Resort - Family-Owned BnB in Crystal Beach, Ontario" },
      { name: "description", content: "Malak BnB Resort is a family-owned bed and breakfast in Crystal Beach, Ontario. Learn the story behind the property and why guests keep coming back to Lake Erie." },
      { property: "og:title", content: "About Malak BnB Resort - Family-Owned BnB in Crystal Beach, Ontario" },
      { property: "og:description", content: "A family-owned boutique BnB in Crystal Beach, Ontario. The story behind Malak BnB Resort and what makes a stay here different." },
      { property: "og:url", content: "https://malakbnbresort.ca/about" },
      { property: "og:image", content: "/images/hero-exterior.webp" },
    ],
    links: [canonical("/about")],
  }),
  component: AboutPage,
});

const VALUES = [
  {
    icon: Heart,
    title: "Family-Owned and Operated",
    desc: "Every guest at Malak is welcomed by the family who built and runs this place. It is not a managed property or a faceless rental. Jay, Hiba, and Gina are here, and that makes the difference.",
  },
  {
    icon: Users,
    title: "Personal Confirmation on Every Booking",
    desc: "We do not use automated booking systems. Every reservation request is reviewed personally before confirmation. You hear from a real person who knows the property and is ready for your arrival.",
  },
  {
    icon: Star,
    title: "Designed for Comfort, Not Just Aesthetics",
    desc: "Gina's eye for decoration is apparent in every room. But beyond the look, the details are practical: good bedding, proper air conditioning, in-room safe, and a breakfast worth waking up for.",
  },
];

function AboutPage() {
  return (
    <div>
      {/* Hero */}
      <section className="relative h-[55vh] min-h-[380px] overflow-hidden">
        <img
          src="/images/hero-exterior.webp"
          alt="Exterior of Malak BnB Resort in Crystal Beach, Ontario"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-foreground/65 via-foreground/20 to-transparent" />
        <div className="relative z-10 flex h-full items-end pb-16 px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-7xl w-full">
            <p className="text-white/70 font-semibold text-sm uppercase tracking-widest mb-3">Our Story</p>
            <h1 className="text-4xl sm:text-5xl font-heading text-white leading-tight max-w-2xl">
              About Malak BnB Resort
            </h1>
          </div>
        </div>
      </section>

      {/* Origin story */}
      <section className="py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div>
              <p className="text-primary font-semibold text-sm uppercase tracking-wider mb-4">How It Started</p>
              <h2 className="text-3xl font-heading text-foreground leading-snug mb-6">
                A Home That Became a Place for Guests
              </h2>
              <div className="space-y-5 text-muted-foreground leading-relaxed">
                <p>
                  Malak BnB Resort did not start as a business plan. It started as a home. Jay and Hiba built this place in Crystal Beach, Ontario with the intention of creating something warm, inviting, and genuinely comfortable. Gina brought her flair for decoration, and somewhere along the way, guests started arriving and not wanting to leave.
                </p>
                <p>
                  That is the thing about a good family-run bed and breakfast. You notice it immediately. The welcome feels personal because it is. The breakfast is made with the same care you would expect in someone's home. The rooms are decorated with actual taste, not the generic neutrals of a chain hotel.
                </p>
                <p>
                  Crystal Beach was the right place for it. It is a lakeside town in the Niagara Region that moves at its own pace. Bay Beach is five minutes on foot. The drive to Niagara Falls is 30 minutes. There is enough to do without the property ever feeling like it needs to compete with the city.
                </p>
                <p>
                  Malak BnB Resort has been welcoming guests since 2018. The reviews tend to say the same things: the hospitality, the food, the way the place makes you feel at home from the moment you walk through the door.
                </p>
              </div>
            </div>
            <div className="rounded-2xl overflow-hidden shadow-xl">
              <img
                src="/images/gallery-staircase-chandelier.webp"
                alt="Grand chandelier and wrought iron staircase inside Malak BnB Resort"
                className="w-full object-cover aspect-[4/5]"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Breakfast photo */}
      <section className="py-20 bg-card">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div className="rounded-2xl overflow-hidden shadow-xl">
              <img
                src="/images/pexels-bnb-breakfast.webp"
                alt="Continental breakfast served at a boutique bed and breakfast"
                className="w-full object-cover aspect-[4/3]"
                loading="lazy"
              />
              <p className="text-xs text-muted-foreground px-4 py-2 bg-card">
                Photo by Jaycee300s via{" "}
                <a href="https://www.pexels.com/photo/breakfast-on-bed-18071824/" target="_blank" rel="noopener noreferrer" className="underline hover:text-foreground">
                  Pexels
                </a>
              </p>
            </div>
            <div>
              <p className="text-primary font-semibold text-sm uppercase tracking-wider mb-4">The Morning Ritual</p>
              <h2 className="text-3xl font-heading text-foreground leading-snug mb-6">
                Breakfast the Way It Should Be
              </h2>
              <div className="space-y-5 text-muted-foreground leading-relaxed">
                <p>
                  One of the first things guests notice is the breakfast. Not a basket of sad pastries slid under the door, but a proper continental breakfast buffet served each morning. Hiba's cooking has a reputation. More than one guest has written that they ended up eating in every night rather than going to a local restaurant, which says something.
                </p>
                <p>
                  The dining room sits beneath a chandelier. The morning light comes in at the right angle. It is the kind of setting that makes you want to sit for a second cup of coffee before you go anywhere.
                </p>
                <p>
                  Dinner service is available for an additional fee, featuring kosher, Middle Eastern, and Italian-inspired cuisine. For guests staying at the property, it turns out to be one of the highlights of the trip.
                </p>
              </div>
              <div className="mt-8 p-5 bg-background rounded-2xl border border-border/50">
                <div className="flex gap-0.5 mb-3">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
                <p className="text-sm text-muted-foreground italic leading-relaxed">
                  "For an additional fee Hiba will cook dinner for you. She is a wonderful cook and hostess. I ended up eating in every night rather than going to a local restaurant."
                </p>
                <p className="mt-3 text-xs font-semibold text-foreground">honint123 - TripAdvisor</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <p className="text-primary font-semibold text-sm uppercase tracking-wider mb-3">What We Believe In</p>
            <h2 className="text-3xl font-heading text-foreground">Why Guests Come Back</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {VALUES.map((v) => (
              <div key={v.title} className="bg-card rounded-2xl p-8 border border-border/50">
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-5">
                  <v.icon className="h-5 w-5 text-primary" />
                </div>
                <h3 className="font-heading text-lg text-foreground mb-3">{v.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Property gallery strip */}
      <section className="py-20 bg-card">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-primary font-semibold text-sm uppercase tracking-wider mb-3">Inside the Property</p>
            <h2 className="text-3xl font-heading text-foreground">What Awaits You at Malak</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { src: "/images/gallery-bedroom-warm.webp", alt: "Warmly lit bedroom at Malak BnB Resort Crystal Beach" },
              { src: "/images/gallery-pool-outdoor.webp", alt: "Outdoor heated pool at Malak BnB Resort" },
              { src: "/images/gallery-dining.webp", alt: "Dining room at Malak BnB Resort with chandelier" },
              { src: "/images/gallery-lounge.webp", alt: "Lounge area at Malak BnB Resort Crystal Beach" },
            ].map((img) => (
              <div key={img.src} className="rounded-2xl overflow-hidden aspect-[4/3]">
                <img src={img.src} alt={img.alt} className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" loading="lazy" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-foreground">
        <div className="mx-auto max-w-3xl px-4 text-center">
          <p className="text-primary-foreground/50 font-semibold text-sm uppercase tracking-widest mb-4">264 Ridge Road South, Crystal Beach, Ontario</p>
          <h2 className="text-3xl sm:text-4xl font-heading text-primary-foreground leading-tight">
            Come and stay with the family.
          </h2>
          <p className="mt-5 text-primary-foreground/70 max-w-lg mx-auto leading-relaxed">
            Submit a reservation request and Jay or Hiba will personally confirm your stay, your room, and your payment details. No automated systems, no third-party fees.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Link
              to="/book"
              className="inline-flex items-center justify-center rounded-full bg-primary px-10 py-4 text-sm font-semibold text-primary-foreground hover:bg-primary/90 transition-colors shadow-lg"
            >
              Request to Book
            </Link>
            <a
              href="tel:+12899298055"
              className="inline-flex items-center gap-2 text-primary-foreground/70 hover:text-primary-foreground text-sm font-medium transition-colors"
            >
              <Phone className="h-4 w-4" />
              1-289-929-8055
            </a>
          </div>
          <Link to="/rooms" className="mt-6 inline-block text-primary-foreground/50 hover:text-primary-foreground text-sm underline transition-colors">
            View rooms and rates
          </Link>
        </div>
      </section>
    </div>
  );
}
